/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useLayoutEffect, useRef } from 'react'
import { Keyboard, Platform, StyleSheet } from 'react-native'

import { zodResolver } from '@hookform/resolvers/zod'
import { useRoute } from '@react-navigation/native'
import { publishEvent } from '@zet/analytics'
import { BottomView } from '@zet/common/components/bottomView'
import { CopilotProvider } from '@zet/common/components/coachMarks'
import { DemoSellAlert } from '@zet/common/components/demoSellAlert'
import KeyBoardWrapper from '@zet/common/components/keyboardWrapper'
import LazyHOC from '@zet/common/components/lazyHoc'
import { useModal } from '@zet/common/hooks/useModal'
import { useNavigation } from '@zet/common/hooks/useNavigation'
import { useSpecificCategory } from '@zet/common/hooks/useSpecificCategory'
import { MY_TEAMS_ADVISOR } from '@zet/constants'
import { IRequestData } from '@zet/customer/src/api/functions/customerAdditionStateMachine'
import {
  useAddCustomerNextState,
  useCustomerCount,
} from '@zet/customer/src/api/hooks'
import useStore from '@zet/customer/src/store'
import useMyCustomersStore from '@zet/customer/src/store/myCustomers'
import { colors as zetColors } from '@zet/design-system'
import { Alert } from '@zet/design-system/alert'
import { Input, InputGroup } from '@zet/design-system/input'
import { Stack } from '@zet/design-system/layout'
import Pressable from '@zet/design-system/pressable'
import UserGroupFilled from '@zet/design-system/svg/UserGroupFilled'
import { B1, B2, B4, B5, BTN, H5 } from '@zet/design-system/typography'
import { APP_NAME } from '@zet/end-points'
import { useExperiments } from '@zet/experiments'
import { useUserContext } from '@zet/feature-user'
import { useSpecificAppComms } from '@zet/feature-user/api/appComms/hooks'
import { i18n } from '@zet/localization'
import { LOANS_CATEGORY } from '@zet/utils/Constants'
import { MOBILE_NUMBER_REGEX, NAME_REGEX } from '@zet/utils/regularExpression'
import {
  MOBILE_NUMBER_VALIDATION,
  NAME_FIELD_EMPTY,
  NAME_FIELD_VALIDATION,
} from '@zet/utils/validationMessage'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'

import CustomerInterest from '../../components/customerInterest'
import { useCoachMarks } from '../../utils/hooks/useCoachmarks'

const validationSchema = z.object({
  fullName: z
    .string()
    .trim()
    .min(1, { message: NAME_FIELD_EMPTY })
    .regex(new RegExp(NAME_REGEX), NAME_FIELD_VALIDATION),
  mobileNumber: z
    .string()
    .min(1, { message: NAME_FIELD_EMPTY })
    .regex(new RegExp(MOBILE_NUMBER_REGEX), MOBILE_NUMBER_VALIDATION)
    .max(10, { message: MOBILE_NUMBER_VALIDATION }),
  email: z.string().email({ message: 'Invalid email' }),
})

const KnowYourCustomer = () => {
  const { params = {} } = useRoute()
  const navigation = useNavigation()
  const { setIsBrandsFlow, customerIntent } = useMyCustomersStore(
    (state) => state,
  )
  const { category } = useSpecificCategory()
  const { data } = useExperiments()
  const routes = navigation.getState()?.routes
  const prevRoute = routes[routes.length - 2]
  const { name, phoneNumber, source, isDemoSale } = params
  const { data: customerCount } = useCustomerCount()

  const {
    CopilotStack,
    CopilotStep,
    customerAdditionSource,
    isComingFromTryDemoSell,
    setIsLayoutRendered,
  } = useCoachMarks({
    source,
    isDemoSale,
  })

  useEffect(() => {
    if (name && phoneNumber) {
      setSelectedCustomer({
        name,
        phoneNumber,
      })
    }
  }, [name, phoneNumber])

  useEffect(() => {
    if (prevRoute?.name !== 'BrandDetails') {
      setIsBrandsFlow(false)
    }
  }, [prevRoute?.name])

  const { data: userData } = useUserContext()
  const { selectedCustomer } = useStore((state) => state)
  const setSelectedCustomer = useStore((state) => state.setSelectedCustomer)
  type ValidationSchema = z.infer<typeof validationSchema>
  const {
    control,
    handleSubmit,
    getValues,
    reset,
    watch,

    formState: { errors, isValid },
  } = useForm<ValidationSchema>({
    resolver: zodResolver(validationSchema),
    mode: 'onChange',

    defaultValues: {
      fullName: !isComingFromTryDemoSell
        ? selectedCustomer?.name
        : userData?.fullName,
      mobileNumber: !isComingFromTryDemoSell
        ? selectedCustomer?.phoneNumber
        : userData?.phoneNumber,
      email: !isComingFromTryDemoSell
        ? selectedCustomer?.email
        : userData?.email,
    },
  })

  useEffect(() => {
    publishEvent('screen_view', {
      screen_name: !isComingFromTryDemoSell
        ? 'KnowYourCustomer'
        : 'prefilled_add_customer',
      is_crif_flow: data?.UI_isAddCustomerNewFlow,
    })
  }, [])

  useEffect(() => {
    if (!isComingFromTryDemoSell)
      reset({
        fullName: selectedCustomer?.name,
        mobileNumber: selectedCustomer?.phoneNumber,
      })
  }, [selectedCustomer])

  const customErrorMap: z.ZodErrorMap = (issue, ctx) => {
    if (issue.code === z.ZodIssueCode.invalid_type) {
      if (issue.expected === 'number') {
        return { message: NAME_FIELD_EMPTY }
      }
    }

    if (issue.code === z.ZodIssueCode.invalid_type) {
      if (issue.expected === 'string') {
        return { message: NAME_FIELD_EMPTY }
      }
    }

    return { message: ctx.defaultError }
  }

  z.setErrorMap(customErrorMap)
  const { fullName, mobileNumber, email } = getValues()

  const {
    mutate: mutateWithExperiments,
    isLoading: isExperimentsLoading,
    isSuccess,
  } = useAddCustomerNextState()

  const getCustomerIntent = () => {
    if (LOANS_CATEGORY.includes(category)) {
      return 'LOANS'
    } else if (category === '15') {
      return 'CC'
    } else {
      return customerIntent
    }
  }

  const onSubmit = () => {
    setSelectedCustomer({
      ...selectedCustomer,
      name: watch('fullName')?.trim(),
      phoneNumber: watch('mobileNumber'),
      email: watch('email')?.trim(),
    })
    const requestBodyData = {
      fullName: watch('fullName')?.trim(),
      phoneNumber: watch('mobileNumber'),
      customerIntent: getCustomerIntent(),
      screenName: 'SHOWN_PHONE_NUMBER_SCREEN',
      customerAdditionSource: customerAdditionSource,
      isDemoSale: isComingFromTryDemoSell,
      email: email?.trim(),
    }
    mutateWithExperiments(requestBodyData as IRequestData)
  }

  useEffect(() => {
    if (isSuccess) {
      setSelectedCustomer({
        ...selectedCustomer,
        name: fullName,
        phoneNumber: mobileNumber,
      })
      publishEvent('click_action', {
        item_name: 'add_customer_phonebook_select_contact',
      })
    }
  }, [isSuccess])

  const inputRef = useRef(null)

  const { data: videoData } = useSpecificAppComms('appVideos')
  const { openModal } = useModal()

  useLayoutEffect(() => {
    setSelectedCustomer(null)
  }, [])

  function handleMyCustomerNavigation() {
    publishEvent('click_action', { item_name: 'select_existing_customer' })
    navigation.navigate('MyCustomers')
  }

  useEffect(() => {
    if (customerCount != undefined) {
      if (customerCount === 0) {
        publishEvent('screen_view', {
          screen_name: 'customer_addition_new_no_customer',
        })
      } else {
        publishEvent('screen_view', {
          screen_name: 'customer_addition_new',
        })
      }
    }
  }, [customerCount])

  return (
    <LazyHOC>
      <KeyBoardWrapper
        className="flex-1 bg-white"
        BottomSheet={
          <>
            <BottomView
              idCardPendingScreen={true}
              onPress={handleSubmit(onSubmit)}
              disabled={!isValid || (!category && !customerIntent)}
              loading={isExperimentsLoading}
              buttonEvent={{
                eventName: 'click_action',
                payload: {
                  item_name: data?.UI_isNewHomepage
                    ? 'continue_customer_addition'
                    : 'add_customer_step_0_continue',
                  customer_name: fullName,
                  customer_phone: mobileNumber,
                  customer_email: email,
                },
              }}
            />
          </>
        }
        scrollViewContentContainerstyle={{
          flexGrow: 1,
          justifyContent: 'space-between',
        }}
      >
        <Stack className="mb-2">
          <Stack className="" onLayout={() => setIsLayoutRendered(true)}>
            <Stack className="h-3" />
            <H5 className="ml-3">{i18n.t('knowCustomer.myCustomer')}</H5>
            {isComingFromTryDemoSell && (
              <Stack className="mt-2">
                <DemoSellAlert
                  content={i18n.t('knowCustomer.warning.saleProcess')}
                />
              </Stack>
            )}
            <Stack className="border-grays-30 border-b px-3 py-2">
              <B5 className="mb-1">{i18n.t('knowCustomer.subHeading1')}</B5>
              {APP_NAME !== MY_TEAMS_ADVISOR && (
                <Pressable
                  className="self-start"
                  onPress={() => {
                    Keyboard.dismiss()
                    openModal('YoutubeVideoModal', {
                      videoData: videoData?.zetMyCustomers,
                    })
                  }}
                  hitSlop={12}
                >
                  <B4 className="text-primary-100">
                    {i18n.t('knowCustomer.subHeading2')}
                  </B4>
                </Pressable>
              )}
            </Stack>
            <CopilotStep
              text={i18n.t('knowCustomer.demoSell.customerName')}
              order={1}
              name="customer_name"
            >
              <CopilotStack className="mx-3 mb-1 mt-3">
                <B2 className="text-grays-80  ml-1">
                  {i18n.t('knowCustomer.customerName')}
                  <B1 className="text-error-100 ml-1">*</B1>
                </B2>
                <Controller
                  control={control}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <Input
                      placeholder="Eg: Ankush"
                      onChangeText={(value) => {
                        onChange(value)
                      }}
                      value={value}
                      type={`${errors.fullName?.message ? 'error' : 'primary'}`}
                      className=" mt-1 "
                      onBlur={onBlur}
                      editable={!isComingFromTryDemoSell}
                    />
                  )}
                  name="fullName"
                />
                {errors.fullName?.message ? (
                  <Stack className="mt-2">
                    <B5 className="text-error-100">
                      {errors.fullName?.message}
                    </B5>
                  </Stack>
                ) : null}
              </CopilotStack>
            </CopilotStep>

            {/* Email */}
            <CopilotStep
              text={i18n.t('knowCustomer.demoSell.customerEmail')}
              order={2}
              name="customer_email"
            >
              <CopilotStack className="mx-3 mb-1 mt-3">
                <B2 className="text-grays-80 mb-1">
                  {Platform.OS === 'web'
                    ? i18n.t('webFlow.basicDetails.email')
                    : i18n.t('customerDetails.email')}
                  <B1 className="text-error-100">*</B1>
                </B2>

                <Controller
                  control={control}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <Input
                      placeholder="Eg: username@email.com"
                      onBlur={onBlur}
                      onChangeText={(value) => {
                        onChange(value)
                      }}
                      keyboardType="email-address"
                      value={value}
                      type={`${errors.email?.message ? 'error' : 'primary'}`}
                    />
                  )}
                  name="email"
                />
                {errors.email?.message ? (
                  <Stack className="mt-2">
                    <B5 className="text-error-100">{errors.email?.message}</B5>
                  </Stack>
                ) : null}
              </CopilotStack>
            </CopilotStep>

            <CopilotStep
              text={i18n.t('knowCustomer.demoSell.customerNumber')}
              order={3}
              name="phone_number"
            >
              <CopilotStack className="mx-3 mt-3">
                <B2 className="text-grays-80 mb-1 ml-1">
                  {i18n.t('knowCustomer.customerPhNum')}
                  <B1 className="text-error-100 ml-1">*</B1>
                </B2>

                <Controller
                  control={control}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <InputGroup
                      placeholder="9000000000"
                      onBlur={onBlur}
                      onChangeText={(value) => {
                        onChange(value)
                        if (value.length === 10) {
                          inputRef.current.blur()
                        }
                      }}
                      value={value}
                      type={`${
                        errors.mobileNumber?.message ? 'error' : 'primary'
                      }`}
                      size="md"
                      maxLength={10}
                      keyboardType="number-pad"
                      ref={inputRef}
                      editable={!isComingFromTryDemoSell}
                      defaultTextStyle="text-grays-70"
                    />
                  )}
                  name="mobileNumber"
                />
                {errors.mobileNumber?.message ? (
                  <Stack className="mt-2">
                    <B5 className="text-error-100">
                      {errors.mobileNumber?.message}
                    </B5>
                  </Stack>
                ) : null}
              </CopilotStack>
            </CopilotStep>
            <Stack className="mx-3 mt-3">
              <Alert
                intent="success"
                iconLeft={{
                  name: 'SecureOutlined',
                  color: zetColors.success[150],
                }}
              >
                {i18n.t('knowCustomer.verify')}
              </Alert>
            </Stack>
          </Stack>
          {!category && (
            <CopilotStep
              text={i18n.t('knowCustomer.demoSell.customerIntent')}
              order={3}
              name="product_selection"
            >
              <CopilotStack className="mx-3">
                <CustomerInterest />
              </CopilotStack>
            </CopilotStep>
          )}
        </Stack>
        <Stack className="mt-5">
          {data?.UI_isNewHomepage &&
            customerCount?.data?.totalCount > 0 &&
            !isComingFromTryDemoSell && (
              <Pressable
                className="border-grays-30 items-center border-t py-4"
                onPress={handleMyCustomerNavigation}
              >
                <Stack
                  className="border-grays-30 absolute rounded-[32] border bg-white px-2 py-1"
                  style={styles.footer}
                >
                  <B5 className="text-grays-80">{i18n.t('knowCustomer.or')}</B5>
                </Stack>
                <Pressable className="flex-row">
                  <UserGroupFilled color={zetColors.primary[50]} />
                  <BTN
                    className="text-primary-100 ml-2"
                    onPress={handleMyCustomerNavigation}
                  >
                    {i18n.t('knowCustomer.selectFrom')}
                  </BTN>
                </Pressable>
              </Pressable>
            )}
        </Stack>
      </KeyBoardWrapper>
    </LazyHOC>
  )
}

const KnowYourCustomerWithCopilot = ({ ...props }) => {
  return (
    <CopilotProvider>
      <KnowYourCustomer {...props} />
    </CopilotProvider>
  )
}

export default KnowYourCustomerWithCopilot

const styles = StyleSheet.create({
  footer: {
    top: -16,
  },
})
